export const commissionModels = [
  {
    type: 'Raw ECN Commission',
    broker: 'ECN / Pro Accounts',
    icon: 'ri-bank-card-line',
    rate: '$3.50 – $7.00 per lot per side',
    structure: 'Per side, per standard lot',
    desc: 'The most transparent model. You pay a fixed dollar amount per lot traded — independent of the spread. The broker passes raw interbank pricing and charges separately.',
    pros: ['Lowest total cost for active traders', 'No markup hidden in the spread', 'True market execution with no dealer intervention'],
    cons: ['Adds up fast for scalpers', 'Higher minimum deposits usually required', 'Can be confusing if you only compare spread numbers'],
    bestFor: 'High-volume traders and scalpers who execute 20+ lots per month',
  },
  {
    type: 'Spread-Only (Commission-Free)',
    broker: 'Standard / Market Maker',
    icon: 'ri-exchange-dollar-line',
    rate: '$0 (built into spread)',
    structure: 'Markup baked into the bid-ask spread',
    desc: 'No separate commission line on your statement. But the spread is wider — the broker earns by marking up the price. You pay anyway, just less visibly.',
    pros: ['Simple — one visible cost', 'Lower minimum deposits', 'No surprise commission bills'],
    cons: ['Higher effective cost per trade', 'Broker is your counterparty (conflict of interest)', 'Hard to calculate your real cost'],
    bestFor: 'Beginners and low-volume traders who want pricing simplicity',
  },
  {
    type: 'Hybrid (Spread + Reduced Commission)',
    broker: 'STP / Hybrid Accounts',
    icon: 'ri-stack-line',
    rate: '$2.00 – $4.00 per lot per side',
    structure: 'Tighter spread than standard, lower commission than ECN',
    desc: 'A middle ground. You get a tighter spread than a commission-free account but also pay a reduced commission. Popular with brokers who want to offer "ECN-like" pricing with lower barriers.',
    pros: ['Lower spread than standard accounts', 'Lower commission than full ECN', 'Good stepping stone from standard to ECN'],
    cons: ['Still not the cheapest total cost', 'Execution quality varies by broker', 'Terms can be confusing'],
    bestFor: 'Intermediate traders transitioning from standard to ECN pricing',
  },
] as const;

export const commissionScenarios = [
  {
    label: 'Scalper',
    tradesPerMonth: 300,
    lotSize: 0.1,
    commissionPerSide: 3.50,
    spreadCostPerTrade: 1.20,
    description: '15 round-turns per day at 0.1 lots. Commission piles up fast.',
  },
  {
    label: 'Day Trader',
    tradesPerMonth: 100,
    lotSize: 0.5,
    commissionPerSide: 3.50,
    spreadCostPerTrade: 1.50,
    description: '5 round-turns per day at 0.5 lots. Balanced between spread and commission.',
  },
  {
    label: 'Swing Trader',
    tradesPerMonth: 20,
    lotSize: 2.0,
    commissionPerSide: 3.50,
    spreadCostPerTrade: 1.00,
    description: '1 trade per day at 2.0 lots. Commission is a small fraction of spread cost.',
  },
] as const;

export const commissionImpact = [
  {
    trader: 'Scalper',
    tradesPerDay: 15,
    lotsPerTrade: 0.1,
    commissionPerSide: 3.50,
    spreadPips: 0.2,
    daysPerMonth: 20,
    tagline: 'Commission is the dominant cost — 3× the spread',
  },
  {
    trader: 'Day Trader',
    tradesPerDay: 5,
    lotsPerTrade: 0.5,
    commissionPerSide: 3.50,
    spreadPips: 0.5,
    daysPerMonth: 20,
    tagline: 'Commission and spread are roughly equal — both matter',
  },
  {
    trader: 'Swing Trader',
    tradesPerDay: 1,
    lotsPerTrade: 2.0,
    commissionPerSide: 3.50,
    spreadPips: 1.0,
    daysPerMonth: 20,
    tagline: 'Spread dominates — commission is secondary',
  },
] as const;

export const commissionFAQs = [
  {
    question: 'What exactly is a forex commission?',
    answer: 'A forex commission is a fixed dollar fee charged per lot traded, separate from the spread. On ECN accounts, you typically pay $3.50–$7.00 per standard lot per side — so a round-turn (open + close) costs $7.00–$14.00. This is charged on top of the raw spread, which is often 0.1–0.3 pips on major pairs. Commission is the broker\'s direct revenue; the raw spread goes to the liquidity providers.',
    category: 'Basics',
  },
  {
    question: 'Why do some brokers charge commission and others do not?',
    answer: 'Commission-free brokers are market makers — they widen the spread to earn their revenue. A "0 commission" EURUSD spread might be 1.2 pips ($12/lot). An ECN broker charges $7 commission but offers a 0.2 pip spread ($2/lot), totaling $9/lot. The commission-free account is actually $3 more expensive — but the cost is hidden in the spread. Nothing is free in forex; you just pay differently.',
    category: 'Basics',
  },
  {
    question: 'How do I calculate commission cost per trade?',
    answer: 'Commission cost = (commission per side × 2) × lot size. For a $3.50/side ECN account trading 0.5 lots: ($3.50 × 2) × 0.5 = $3.50 per round-turn. Add the spread cost: 0.2 pips × $10/pip × 0.5 lots = $1.00. Total cost = $4.50. Compare this to a commission-free account at 1.2 pips: 1.2 × $10 × 0.5 = $6.00. The ECN account saves $1.50 per trade.',
    category: 'Calculation',
  },
  {
    question: 'Are commissions negotiable with forex brokers?',
    answer: 'Yes — but only if you trade significant volume. Most brokers start reducing commissions at 100+ lots per month, with tiered discounts at 500, 1,000, and 5,000 lot levels. An institutional account trading 10,000+ lots per month might pay $1.50–$2.00 per side instead of $3.50. For retail traders doing under 50 lots per month, the advertised rate is usually non-negotiable. Always ask — the worst they can say is no.',
    category: 'Broker Differences',
  },
  {
    question: 'Does commission change based on the currency pair?',
    answer: 'It depends on the broker. Some charge a flat dollar rate regardless of pair — $7 per lot on EURUSD, GBPJPY, and XAUUSD all the same. Others adjust: major pairs at $7, minors at $10, exotics at $15+. CFD indices and commodities also have different commission structures. Always check the contract specifications for each instrument before trading — do not assume the EURUSD rate applies everywhere.',
    category: 'Broker Differences',
  },
  {
    question: 'What is the difference between per-side and per-round-turn commission?',
    answer: 'Per-side means you pay when you open AND when you close. A $3.50/side rate = $7.00 round-turn. Per-round-turn means the single number covers both — a $7.00/RT rate = $7.00 total. Most ECN brokers quote per-side because it makes the number look smaller. When comparing brokers, always convert to round-turn: multiply per-side rates by 2, or just compare the total cost of a complete trade.',
    category: 'Basics',
  },
  {
    question: 'How does commission affect scalping profitability?',
    answer: 'Massively. A scalper doing 15 round-turns daily at 0.1 lots with $3.50/side commission pays: 15 × 0.1 × $7.00 = $10.50/day just in commission. At 20 trading days, that is $210/month. Their spread cost at 0.2 pips is only 15 × 0.1 × $0.20 × 20 = $6/month. Commission is 35× larger than spread cost for this scalper. This is why scalpers obsess over commission rates — it is quite literally their biggest expense.',
    category: 'Trading Impact',
  },
  {
    question: 'Do Islamic (swap-free) accounts have different commissions?',
    answer: 'Often yes. Since the broker cannot earn swap revenue on Islamic accounts, many compensate by charging a higher commission or widening the spread. An account that normally charges $3.50/side might charge $5.00/side on the swap-free version. Some brokers charge a flat admin fee instead. Always check the Islamic account terms separately — do not assume the standard commission rate applies.',
    category: 'Broker Differences',
  },
  {
    question: 'Can commission be avoided entirely?',
    answer: 'No — but you can choose how you pay it. Commission-free accounts build the cost into a wider spread. ECN accounts charge it transparently. Either way, every trade has a cost. The real question is: which model gives you the lowest total cost for your specific trading volume and style? For most active traders, the transparent ECN commission model is cheaper — but you must run the numbers for your own trading.',
    category: 'Basics',
  },
  {
    question: 'How do I compare total costs between commission and commission-free accounts?',
    answer: 'Use this formula: Total Cost = (Spread in pips × Pip Value) + (Commission per side × 2). For EURUSD at 1 standard lot: Commission-free at 1.2 pips = 1.2 × $10 + $0 = $12.00. ECN at 0.2 pips + $3.50/side = 0.2 × $10 + ($3.50 × 2) = $2.00 + $7.00 = $9.00. The ECN account is 25% cheaper. Always calculate the total cost — never compare commission rates in isolation.',
    category: 'Calculation',
  },
  {
    question: 'Are there any hidden commission costs I should watch for?',
    answer: 'Yes. Watch for: (1) minimum commission per trade — some brokers charge a floor of $5 even on micro lots, (2) different rates for different instruments — CFD indices often have higher commissions than forex, (3) inactivity fees on dormant accounts, (4) conversion fees if your account currency differs from the traded pair\'s quote currency. The advertised $3.50/side rate is the headline — read the fine print for the full picture.',
    category: 'Hidden Costs',
  },
  {
    question: 'How do swap-free accounts handle commission differently?',
    answer: 'Since the broker cannot earn overnight swap revenue on Islamic accounts, most compensate through one of three methods: (1) higher commission — $5.00/side instead of $3.50, (2) wider spreads — adding 0.3–0.5 pips to the raw spread, or (3) a flat weekly admin fee — $5–$10 per lot held over Wednesday. Check your broker\'s Islamic account terms carefully; the cost difference can be substantial for swing traders who hold positions for weeks.',
    category: 'Broker Differences',
  },
] as const;

export const relatedCommissionGuides = [
  {
    title: 'Spread',
    href: '/forex-spread/',
    desc: 'Every trade starts in the red because of the bid-ask spread. Here\'s how wide it gets, what moves it, and how much it really costs you.',
    icon: 'ri-arrow-left-right-line',
  },
  {
    title: 'Swap / Rollover',
    href: '/forex-swap/',
    desc: 'Hold a trade past midnight and the broker charges (or pays) interest. For swing traders, this can easily outweigh both spread and commission.',
    icon: 'ri-moon-clear-line',
  },
  {
    title: 'Slippage',
    href: '/forex-slippage/',
    desc: 'Fast markets mean bad fills. Here\'s why the price you click and the price you get are rarely the same — and when it hurts most.',
    icon: 'ri-speed-up-line',
  },
  {
    title: 'Hidden Charges',
    href: '/hidden-forex-trading-costs/',
    desc: 'Not on your trade ticket but still coming out of your pocket — conversion markups, withdrawal fees, and those sneaky inactivity charges.',
    icon: 'ri-eye-off-line',
  },
] as const;

export const brokerCommissionRates = [
  {
    broker: 'IC Markets',
    account: 'Raw Spread (cTrader)',
    commissionPerSide: 3.00,
    commissionRT: 6.00,
    minDeposit: 200,
    instruments: 'Forex + Metals',
    highlight: true,
    notes: 'Commission is charged in the base currency of the traded pair — so EURUSD commission is in EUR, not USD. This can make cost tracking slightly trickier but the rate itself is competitive.',
  },
  {
    broker: 'Pepperstone',
    account: 'Razor',
    commissionPerSide: 3.50,
    commissionRT: 7.00,
    minDeposit: 200,
    instruments: 'Forex + Metals',
    highlight: false,
    notes: 'Commission drops to $2.50/side at 150+ lots/month through their Active Trader program. AUD-denominated accounts pay slightly more due to currency conversion on the commission.',
  },
  {
    broker: 'FP Markets',
    account: 'Raw ECN',
    commissionPerSide: 3.00,
    commissionRT: 6.00,
    minDeposit: 100,
    instruments: 'Forex + Metals + Indices',
    highlight: true,
    notes: 'One of the few brokers offering $3.00/side across forex, metals, and CFD indices on the same account. No minimum commission per trade — micro lot traders benefit.',
  },
  {
    broker: 'ThinkMarkets',
    account: 'ThinkZero',
    commissionPerSide: 3.50,
    commissionRT: 7.00,
    minDeposit: 100,
    instruments: 'Forex + Metals',
    highlight: false,
    notes: 'Multi-regulated (ASIC, FCA, FSA) with global availability. ThinkZero is the raw spread account — spreads from 0.0 pips with flat $3.50/side commission. Indices and shares trade on a spread-only basis with no commission on this account.',
  },
  {
    broker: 'FxPro',
    account: 'cTrader',
    commissionPerSide: 3.50,
    commissionRT: 7.00,
    minDeposit: 100,
    instruments: 'Forex + Metals',
    highlight: false,
    notes: 'Commission is $35 per $1M traded, which works out to $3.50/side on a standard lot. FCA-regulated with strong execution quality — you pay the same rate as Pepperstone/Eightcap but with tighter regulatory oversight.',
  },
  {
    broker: 'Vantage',
    account: 'Raw ECN',
    commissionPerSide: 3.00,
    commissionRT: 6.00,
    minDeposit: 50,
    instruments: 'Forex + Metals + Indices',
    highlight: true,
    notes: 'Matches IC Markets and FP Markets on the headline rate. The $50 minimum deposit is the lowest in this bracket. Active trader rebates start at just 50 lots/month.',
  },
  {
    broker: 'Eightcap',
    account: 'Raw',
    commissionPerSide: 3.50,
    commissionRT: 7.00,
    minDeposit: 100,
    instruments: 'Forex + Metals',
    highlight: false,
    notes: 'Solid mid-range option with reliable execution. The commission doesn\'t scale down with volume as aggressively as competitors, so heavy traders might find better value elsewhere.',
  },
  {
    broker: 'Tickmill',
    account: 'Raw',
    commissionPerSide: 3.00,
    commissionRT: 6.00,
    minDeposit: 100,
    instruments: 'Forex + Metals',
    highlight: false,
    notes: 'Pro account rebranded to Raw in late 2025. Commission went from $2.00 to $3.00/side — now on par with IC Markets and FP Markets.',
  },
] as const;

export const commissionReductionTips = [
  {
    title: 'Ask for a volume discount',
    icon: 'ri-chat-3-line',
    desc: 'Most brokers publish tiered rates starting at 100 lots/month. But even at 50 lots/month, a polite email to your account manager often gets you a 10–20% reduction. Brokers value retention — the worst they can say is no.',
    impact: 'High',
  },
  {
    title: 'Open a PAMM or MAM account',
    icon: 'ri-group-line',
    desc: 'If you manage money for multiple clients, pooled volume under a PAMM/MAM structure counts toward higher tiers. Many brokers also offer custom commission schedules for money managers that are significantly below the advertised rate.',
    impact: 'High',
  },
  {
    title: 'Switch to a different account type',
    icon: 'ri-arrow-left-right-line',
    desc: 'Moving from a retail ECN account to a professional or institutional account tier often drops commission from $3.50 to $2.00–$2.50 per side. The tradeoff is usually a higher minimum deposit — but the savings can pay for that within months.',
    impact: 'Medium',
  },
  {
    title: 'Trade higher timeframes',
    icon: 'ri-time-line',
    desc: 'The simplest way to reduce commission? Trade less. Moving from 15 scalps a day to 5 swing trades a month can cut your monthly commission by 90%+. If commission is eating your profits, trading less frequently — with bigger position sizes on higher timeframes — is the most direct fix.',
    impact: 'High',
  },
  {
    title: 'Negotiate a rebate program',
    icon: 'ri-refund-2-line',
    desc: 'Some brokers offer cash rebates instead of lower upfront commission — for example, $0.50/lot rebate paid monthly. The effective net commission drops without the broker having to change their published rate. Ask specifically about rebates, not just rate discounts.',
    impact: 'Medium',
  },
  {
    title: 'Bundle services for better rates',
    icon: 'ri-links-line',
    desc: 'Using the same broker for forex, CFD indices, and crypto can qualify you for blended volume discounts. A trader doing 30 forex lots + 20 index lots/month may cross a tier threshold that neither volume alone would reach. Consolidate accounts at one broker where possible.',
    impact: 'Medium',
  },
] as const;